<template>
    <v-layout fill-height :class="{'d-flex flex-column': isMobile}">
        <mobile-booking-page-tabs v-if="$breakpoint.smAndDown" v-model="mobileTabs" />
        <v-container fluid>
            <slot name="breadcrumbs" />
            <v-row v-if="loaded">
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 0)" md="8">
                    <client-only>
                        <agency-reference
                            v-if="showAgencyReference"
                            v-model="agentRefNumber"
                            :disabled="disableAgentRefNumber"
                            class="mb-4"
                        />
                        <v-card v-show="showPrivateClientSelector" variant="outlined" class="mb-4">
                            <v-card-text>
                                <tourist-autocomplete
                                    :person-id="selectedPrivateClientId"
                                    :company-id="companyId"
                                    can-update
                                    :tourist="tourLead"
                                    :disabled="!!bookingKey || !!basketKey || !!selectedPrivateClientId"
                                    @change="setTourLead($event, true)"
                                />
                            </v-card-text>
                        </v-card>
                        <v-form v-if="hasBookingFields" ref="mainForm">
                            <account-booking-info
                                v-if="isShowAccountBookingInfo"
                                :disabled="!!bookingKey || !!basketKey"
                            />
                            <template v-if="memberLevel !== 'AUTH' || person">
                                <div v-if="formTitle" class="text-subtitle-2 text-info mb-4">
                                    {{ formTitle }}
                                </div>
                                <div style="position: relative">
                                    <slot
                                        name="touristsCard"
                                        :tourists="tourists"
                                        :booking-key="bookingKey"
                                        :basket-key="basketKey"
                                        :page-lock="pageLock"
                                        :extra-services="extraServices"
                                        :is-saving-without-details="isSavingWithoutDetails"
                                        :disabled="!!bookingKey || !!basketKey"
                                        :person-selected="personSelected"
                                        :form-refs="formRefs"
                                        :clear-booking-key="clearBookingKey"
                                    >
                                        <v-card variant="outlined" class="touristsCard-default mb-5">
                                            <v-card-title class="text-capitalize">
                                                <v-icon start color="primary">mdi-account</v-icon>
                                                {{ $t(`${touristToken}s`) }}
                                            </v-card-title>
                                            <v-divider />
                                            <v-card-text>
                                                <div
                                                    v-for="(tourist, index) in tourists"
                                                    :key="index"
                                                    :class="{'mb-8': index !== tourists.length - 1}"
                                                >
                                                    <slot
                                                        name="tourists"
                                                        :disabled="!!bookingKey || !!basketKey"
                                                        :index="index"
                                                        :tourists="tourists"
                                                        :person-selected="personSelected"
                                                        :is-saving-without-details="isSavingWithoutDetails"
                                                        :form-refs="formRefs"
                                                    >
                                                        <div :data-cy="`tourist-${index}`">
                                                            <tourist-form
                                                                :ref="el => formRefs.push(el)"
                                                                :key="index"
                                                                v-model="tourists[index]"
                                                                :index="index"
                                                                :label="touristToken"
                                                                :disabled="!!bookingKey || !!basketKey"
                                                                :corporate-policy-violation="
                                                                    offer ? offer.corporatePolicyViolation : undefined
                                                                "
                                                                :hide-index="tourists.length === 1"
                                                                :selected-tourists="tourists"
                                                                :start-date="startDate"
                                                                :disable-required-rule="isSavingWithoutDetails"
                                                                class="mb-4"
                                                                @person-selected="personSelected($event)"
                                                            />
                                                            <tourist-passport-form
                                                                v-if="touristPassportForm"
                                                                :ref="el => formRefs.push(el)"
                                                                :key="`ptf_${index}`"
                                                                :index="index"
                                                                :disabled="!!bookingKey || !!basketKey"
                                                                :corporate-policy-violation="
                                                                    offer ? offer.corporatePolicyViolation : undefined
                                                                "
                                                                :disable-required-rule="isSavingWithoutDetails"
                                                                expanded-all
                                                            />
                                                        </div>
                                                    </slot>
                                                </div>
                                            </v-card-text>
                                        </v-card>
                                    </slot>
                                    <slot
                                        name="forms"
                                        :booking-key="bookingKey"
                                        :basket-key="basketKey"
                                        :extra-services="extraServices"
                                        :form-refs="formRefs"
                                        :is-saving-without-details="isSavingWithoutDetails"
                                        :clear-booking-key="clearBookingKey"
                                    />
                                    <extra-services-form
                                        v-if="
                                            bookingAdditionalOptions &&
                                            bookingAdditionalOptions.extraServices &&
                                            bookingAdditionalOptions.extraServices.length
                                        "
                                        v-model="extraServices"
                                        :additional-options="bookingAdditionalOptions"
                                        :disabled="!!bookingKey || !!basketKey"
                                        @update="clearBookingKey"
                                    />
                                    <corporate-violation-reason
                                        v-if="corporatePolicyViolated && corporatePolicyType"
                                        v-model="corporatePolicyViolationCodeId"
                                        :type="corporatePolicyType"
                                        :policies="violatedPolicies"
                                        :disabled="!!bookingKey || !!basketKey"
                                        class="mb-2"
                                    />
                                    <add-on-booking-forms
                                        :selected-add-ons="selectedAddOns"
                                        :disabled="isLoading || pageLock || !!bookingKey || !!basketKey"
                                    />
                                    <add-ons
                                        v-if="showAddOns"
                                        :offer-key="offerKeys ? offerKeys[0] : $route.query.offerKey"
                                        :initial-tourist-booking-fields="touristBookingFields"
                                        class="pb-5"
                                        :disabled="
                                            isLoading || pageLock || !!bookingKey || !!basketKey || !resolveLoyalty
                                        "
                                        :product-type="productType"
                                        :offer="offer"
                                        @select="selectAddOn"
                                    />
                                    <loyalty-points
                                        v-if="isB2C"
                                        ref="loyaltyPoints"
                                        v-model="resolveLoyalty"
                                        :is-loading="isLoading"
                                        :disabled="isLoading || pageLock || !!bookingKey || !!basketKey"
                                    />
                                    <slot
                                        name="orderCommentsForm"
                                        :booking-key="bookingKey"
                                        :basket-key="basketKey"
                                        :form-refs="formRefs"
                                    />
                                    <v-btn
                                        v-if="(bookingKey || basketKey) && !pageLock && !isSavingWithoutDetails"
                                        position="absolute"
                                        icon="mdi-pencil"
                                        location="bottom right"
                                        size="small"
                                        color="primary"
                                        class="mb-4"
                                        @click="resetBookResponse"
                                    />
                                </div>
                                <template v-if="!isSavingWithoutDetails">
                                    <payment-options
                                        v-if="(bookingKey || basketKey) && canPay"
                                        v-model="paymentMethodId"
                                        :disabled="isLoading || pageLock"
                                        :_number-of-payments="numberOfPayments"
                                        outlined
                                        class="mb-5"
                                        @change-number-of-payments="numberOfPayments = $event"
                                    />
                                    <div id="dropin-container" />
                                    <n-m-i-payment-form
                                        v-if="nmiPaymentSuccessUrl"
                                        @finish-submit="onNMIPaymentFinish"
                                    />
                                    <div id="paymentTokenInfo" />
                                    <booking-agreement-form
                                        v-if="bookingKey || basketKey"
                                        v-model="isAgreementsConfirmed"
                                        :country-id="searchRequestCountryId"
                                        :_due-to-confirm-date="dueToConfirmDate"
                                        :_due-to-confirm-date-active="dueToConfirmDateActive"
                                        :start-date="startDate"
                                        :selected-add-ons="selectedAddOns"
                                        :room-offers="roomOffers"
                                        @change-due-to-confirm-date="dueToConfirmDate = $event"
                                        @change-due-to-confirm-date-active="dueToConfirmDateActive = $event"
                                    />
                                </template>
                                <booking-action-btns
                                    :booking-key
                                    :basket-key
                                    :is-saving-without-details
                                    :can-save-without-details
                                    :is-mobile
                                    :is-loading
                                    :page-lock
                                    :next-step-label
                                    :is-save-method-button-text-style
                                    :save-method
                                    :can-pay
                                    :payment-method-id
                                    :is-agreements-confirmed
                                    :due-to-confirm-date-active
                                    @prepare-book="prepareBook"
                                    @save-without-details="saveWithoutDetails"
                                    @book="book"
                                    @save="save"
                                />
                            </template>
                        </v-form>
                    </client-only>
                </v-col>
                <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 1)" md="4">
                    <slot
                        name="sidebar"
                        :booking-key="bookingKey"
                        :basket-key="basketKey"
                        :selected-add-ons="selectedAddOns"
                        :extra-services="extraServices"
                        :tourists="tourists"
                        :prepare-book-response="prepareBookResponse"
                    >
                        <order-summary-layout
                            :total-price="totalPrice"
                            :product-store="productStore"
                            :extra-prices="extraPrices"
                            :room-offers="roomOffers"
                            :search-request="searchRequest"
                            :offer="offer"
                        >
                            <slot
                                name="orderSummary"
                                :booking-key="bookingKey"
                                :basket-key="basketKey"
                                :selected-add-ons="selectedAddOns"
                                :extra-services="extraServices"
                                :tourists="tourists"
                                :selected-meal-types="selectedMealTypes"
                            />
                            <template v-slot:violatedPolicies>
                                <slot
                                    name="violatedPolicies"
                                    :corporate-policy-violated="corporatePolicyViolated"
                                    :violated-policies="violatedPolicies"
                                />
                            </template>
                        </order-summary-layout>
                    </slot>
                </v-col>
            </v-row>
            <v-layout v-else fill-height class="align-center justify-center">
                <v-progress-circular indeterminate size="64" color="primary" />
            </v-layout>
        </v-container>
        <payment-redirect-form ref="paymentRedirectForm" />
        <prepare-book-messages-modal
            v-if="isPrepareBookResponseExist"
            ref="prepareBookMessagesModal"
            :prepare-book-response="prepareBookResponse"
            :offers-data="offersData"
            :is-saving-without-details="isSavingWithoutDetails"
            @approve="isSavingWithoutDetails ? bookWithoutDetails() : paymentStepInit()"
            @cancel="cancelFlow"
            @answers-changed="onAnswersChanged"
        />
        <slot />
    </v-layout>
</template>

<script lang="ts">
    import {Component, Prop} from 'vue-facing-decorator'
    import MobileBookingPageTabs from '@/components/snippets/MobileBookingPageTabs.vue'
    import TouristForm from '~src/components/booking/forms/touristForm.src'
    import AccountBookingInfo from '~src/components/booking/accountBookingInfo.src'
    import PaymentOptions from '@/components/booking/PaymentOptions.vue'
    import BookingAgreementForm from '@/components/booking/forms/BookingAgreementForm.vue'
    import PaymentRedirectForm from '@/components/booking/forms/PaymentRedirectForm.vue'
    import ExtraServicesForm from '~src/components/booking/forms/ExtraServicesForm.vue'
    import LoyaltyPoints from '@/components/booking/LoyaltyPoints.vue'
    import '@adyen/adyen-web/dist/adyen.css'
    import AddOns from '@/components/booking/addOns/AddOns.vue'
    import AddOnBookingForms from '@/components/booking/addOns/AddOnBookingForms.vue'
    import PrepareBookMessagesModal from '~src/components/booking/modals/PrepareBookMessagesModal.vue'
    import {BookingPageMixin} from '~src/mixins/BookingPageMixin.vue'
    import TouristPassportForm from '~src/components/booking/forms/touristPassportForm.src'
    import OrderSummaryLayout from '~src/components/booking/orderSummaryLayout.src'
    import CorporateViolationReason from '~/components/booking/forms/fields/CorporateViolationReason.vue'
    import TouristAutocomplete from '@/components/parts/Booking/TouristAutocomplete.vue'
    import AgencyReference from '~src/components/parts/Booking/AgencyReference.vue'
    import NMIPaymentForm from '~/components/booking/forms/NMIPaymentForm.vue'
    import BookingActionBtns from '~src/components/parts/Booking/BookingActionBtns.vue'

    @Component({
        components: {
            OrderSummaryLayout,
            TouristPassportForm,
            PrepareBookMessagesModal,
            AddOnBookingForms,
            AddOns,
            ExtraServicesForm,
            PaymentRedirectForm,
            BookingAgreementForm,
            MobileBookingPageTabs,
            AccountBookingInfo,
            TouristForm,
            PaymentOptions,
            CorporateViolationReason,
            TouristAutocomplete,
            AgencyReference,
            LoyaltyPoints,
            NMIPaymentForm,
            BookingActionBtns,
        },
    })
    export class BookingPageBase extends BookingPageMixin {
        @Prop({required: true}) searchRequest
        @Prop({required: true}) offer
        @Prop({required: false}) searchRequestCountryId
        @Prop() offerKeys
        @Prop({default: false, type: Boolean}) touristPassportForm
        @Prop() roomOffers

        async mounted() {
            await this.load()
        }

        clearBookingKey() {
            this.bookingKey = null
            this.basketKey = null
        }

        get selectedPrivateClientId() {
            return persistentStore.selectedPrivateClient?.personId
        }

        get companyId() {
            return persistentStore.selectedCompanyAndAgent?.company.id || authStore.person?.companyId
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get prebooked() {
            return !!this.bookingKey || !!this.basketKey
        }

        get showAgencyReference() {
            if (
                this.$conf.public.reservationFilters.agentRefNumber &&
                this.isTO1 &&
                persistentStore.selectedCompanyAndAgent?.agent?.role
            ) {
                const role = persistentStore.selectedCompanyAndAgent.agent.role.split('.')[0]

                return role === 'to' || role === 'ta'
            }

            return this.$conf.public.reservationFilters.agentRefNumber && (this.isTO2 || this.isAgency)
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get nextStepLabel() {
            return this.$t('next_step')
        }
    }

    export default BookingPageBase
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    :deep(.adyen-checkout__payment-methods-list) {
        padding-left: 0 !important;
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        .save-method-book-button {
            padding: 0 !important;
            font-size: 0.65rem;
        }
    }
</style>
